import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { CompanySettings } from '@/types';
import type { CompanyRow } from '@/types/supabase';
import { getOfflineCache, setOfflineCache } from '../lib/offlineCache';
import { isNetworkError } from '../utils/network';

function textFromSettings(json: Record<string, unknown> | null | undefined, key: string): string {
    return json && json[key] ? String(json[key]) : '';
}

export const SettingsService = {
    async getSettings(): Promise<CompanySettings | null> {
        try {
            const { data, error } = await supabase
                .from('companies')
                .select('*')
                .limit(1)
                .single();

            if (error) {
                // PGRST116: Not Found is NOT a connection error. 
                // It means the company has been deleted or no company exists yet.
                if (error.code === 'PGRST116' || error.code === '406' || error.message?.includes('0 rows')) {
                    logger.warn('SettingsService: No company found (PGRST116).');
                    return null;
                }
                
                logger.error('CRITICAL: Error fetching settings', error);
                throw error; // Let the caller/query handle real errors
            }

            const company = data as CompanyRow;
            const settingsJson = company.settings || {};
            const result: CompanySettings = {
                id: company.id,
                name: company.name,
                companyLogo: textFromSettings(settingsJson, 'companyLogo') || '',
                notifications: {
                    adminEmail: company.admin_email || textFromSettings(settingsJson, 'adminEmail') || '',
                    adminPhone: textFromSettings(settingsJson, 'adminPhone'),
                    materialChannels: settingsJson.materialChannels || { email: false, whatsapp: false, sms: false },
                    issueChannels: settingsJson.issueChannels || { email: false, whatsapp: false, sms: false },
                },
                specialtyGroups: settingsJson.specialtyGroups || [],
                soloMode: !!settingsJson.soloMode,
                workDayStart: textFromSettings(settingsJson, 'workDayStart') || '08:00',
                workDayEnd: textFromSettings(settingsJson, 'workDayEnd') || '18:00',
                subscriptionStatus: company.subscription_status || undefined,
                currentTierId: company.current_tier_id || undefined,
                onboardingCompleted: !!settingsJson.onboarding_completed,
                quickTemplatesConfig: (settingsJson.quickTemplatesConfig as any) || undefined,
            };

            await setOfflineCache('pd_cached_settings', result);
            return result;
        } catch (err) {
            if (isNetworkError(err)) {
                const cached = await getOfflineCache('pd_cached_settings');
                if (cached) {
                    logger.info('SettingsService: Restored settings from offline cache');
                    return cached;
                }
            }
            throw err;
        }
    },

    async completeOnboarding(companyName: string, adminName: string): Promise<void> {
        const {
            data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;

        if (!user) throw new Error('No authenticated user found (Session)');

        const { data, error } = await supabase.rpc('complete_onboarding', {
            p_company_name: companyName,
            p_admin_name: adminName,
            p_user_id: user.id,
            p_email: user.email,
        });

        if (error) {
            logger.error('RPC Onboarding Error', error);
            throw new Error('Error al configurar la cuenta: ' + error.message);
        }

        if (!data || !data.success) {
            throw new Error('Error desconocido al crear la cuenta.');
        }
    },

    async updateSettings(settings: CompanySettings): Promise<void> {
        const settingsJson = {
            adminPhone: settings.notifications?.adminPhone ?? '',
            materialChannels: settings.notifications?.materialChannels ?? { email: false, whatsapp: false, sms: false },
            issueChannels: settings.notifications?.issueChannels ?? { email: false, whatsapp: false, sms: false },
            specialtyGroups: settings.specialtyGroups ?? [],
            specialties: settings.specialties ?? [],
            soloMode: !!settings.soloMode,
            companyLogo: settings.companyLogo ?? '',
            workDayStart: settings.workDayStart ?? '08:00',
            workDayEnd: settings.workDayEnd ?? '18:00',
            onboarding_completed: !!settings.onboardingCompleted, // V2199: Preserve flag
            quickTemplatesConfig: settings.quickTemplatesConfig,
        };

        const { data, error } = await supabase.rpc('update_my_company_settings', {
            p_name: settings.name,
            p_admin_email: settings.notifications?.adminEmail ?? '',
            p_settings: settingsJson,
        });

        if (error) {
            logger.error('SettingsService: RPC Error', error);
            const msg = error.message || 'No se pudieron guardar los cambios.';
            if (msg.includes('sin empresa asignada')) {
                throw new Error('Tu usuario no tiene empresa asignada. Contacta al administrador o completa el onboarding.');
            }
            throw new Error(msg);
        }
        if (!data?.success) {
            logger.error('SettingsService: RPC responded without success', data);
            throw new Error('No se pudo actualizar la empresa. Comprueba que tienes permiso o que la empresa existe.');
        }
    },

    async deleteAccount(): Promise<void> {
        const { data, error } = await supabase.rpc('delete_my_company', {});

        if (error) {
            logger.error('SettingsService: Error deleting account', error);
            throw new Error(error.message || 'Error al eliminar la cuenta');
        }

        if (!data?.success) {
            throw new Error(data?.message || 'No se pudo eliminar la cuenta');
        }
    },
};

